/**
 * Login-Page — Magic-Link-Flow.
 *
 * UX:
 *   1. Email eingeben → "Login-Link anfordern"
 *   2a. Normaler Betrieb: "Prüfe deine Email" — fertig.
 *   2b. Dev-Bypass aktiv: Server antwortet mit `verifyUrl` → wir zeigen
 *       einen "Direkt einloggen"-Button, der den Token sofort einlöst.
 *       Kein Email-Provider nötig.
 *
 * Der Bypass-Token liegt in localStorage (`sc.authBypass`). Ein kleiner
 * "Dev-Zugang"-Collapse erlaubt Eingabe/Entfernen, damit wir während der
 * Test-Phase nicht die DevTools-Console bemühen müssen.
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ApiError,
  getAuthBypass,
  requestMagicLink,
  setAuthBypass,
  verifyMagicLink,
  type MagicLinkResponse,
} from "../lib/api";
import { useAuth } from "../lib/AuthContext";

export default function Login() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh, user } = useAuth();

  const [email, setEmail] = useState(params.get("email") ?? "");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<MagicLinkResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Bypass-Toggle
  const [showBypass, setShowBypass] = useState(false);
  const [bypassInput, setBypassInput] = useState(getAuthBypass() ?? "");
  const bypassConfigured = !!getAuthBypass();

  useEffect(() => {
    if (user) nav("/dashboard", { replace: true });
  }, [user, nav]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const r = await requestMagicLink({
        email: email.trim().toLowerCase(),
        displayName: displayName.trim() || undefined,
      });
      setResponse(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  async function verifyNow() {
    if (!response?.verifyUrl) return;
    setLoading(true);
    setError(null);
    try {
      const u = new URL(response.verifyUrl);
      const token = u.searchParams.get("token");
      const emailParam = u.searchParams.get("email");
      if (!token || !emailParam) throw new Error("verifyUrl fehlerhaft");
      await verifyMagicLink(token, emailParam);
      await refresh();
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  function saveBypass() {
    setAuthBypass(bypassInput.trim() || null);
    // Force re-render der bypassConfigured-Anzeige
    setShowBypass(false);
    setShowBypass(true);
  }

  return (
    <div className="max-w-md mx-auto py-12">
      <h1 className="text-3xl font-bold tracking-tight mb-2">Login</h1>
      <p className="text-slate-400 mb-8 text-sm">
        Gib deine Email ein — wir schicken dir einen passwortlosen Login-Link.
      </p>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-sm text-slate-300 mb-1">Email</label>
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-court-500"
            placeholder="name@example.com"
          />
        </div>
        <div>
          <label className="block text-sm text-slate-300 mb-1">
            Name <span className="text-slate-500">(optional, nur bei erstem Login)</span>
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-court-500"
            placeholder="z.B. Intercosmetics"
          />
        </div>

        <button
          type="submit"
          disabled={loading || !email}
          className="w-full rounded-lg bg-gradient-to-r from-court-500 to-court-700 hover:from-court-400 hover:to-court-600 disabled:opacity-50 px-4 py-3 font-semibold transition shadow-lg shadow-court-700/30"
        >
          {loading ? "Bitte warten…" : "Login-Link anfordern"}
        </button>
      </form>

      {error && (
        <div className="mt-4 rounded-lg bg-red-950/50 border border-red-800 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      {response && !response.devBypass && (
        <div className="mt-6 rounded-lg bg-emerald-950/40 border border-emerald-800 px-4 py-3 text-sm text-emerald-200">
          <p className="font-semibold">✓ Prüfe deine Email</p>
          <p className="mt-1 text-emerald-300/80">
            Falls für diese Adresse ein Konto existiert, haben wir einen Login-Link verschickt (
            {Math.round(response.expiresInSeconds / 60)} Minuten gültig).
          </p>
        </div>
      )}

      {response && response.devBypass && response.verifyUrl && (
        <div className="mt-6 rounded-lg bg-amber-950/40 border border-amber-700 px-4 py-3 text-sm text-amber-100 space-y-3">
          <p className="font-semibold">⚡ Dev-Bypass aktiv</p>
          <p className="text-amber-200/80">
            Kein Email-Versand. Der Magic-Link wurde direkt übermittelt — ein Klick und du bist drin.
          </p>
          <button
            onClick={verifyNow}
            disabled={loading}
            className="w-full rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-50 px-4 py-2 font-semibold transition text-slate-950"
          >
            {loading ? "Login läuft…" : "Direkt einloggen"}
          </button>
        </div>
      )}

      {/* --- Dev-Access Collapsible --- */}
      <div className="mt-10 border-t border-slate-800 pt-6">
        <button
          onClick={() => setShowBypass((v) => !v)}
          className="text-xs text-slate-500 hover:text-slate-300 transition font-mono"
        >
          {showBypass ? "▼" : "▶"} Dev-Zugang {bypassConfigured ? "· aktiv" : ""}
        </button>
        {showBypass && (
          <div className="mt-3 rounded-lg border border-slate-800 bg-slate-950/60 p-4 text-xs space-y-3">
            <p className="text-slate-400 leading-relaxed">
              Für die Testphase: trage dein <code className="bg-slate-900 px-1 py-0.5 rounded">AUTH_DEV_BYPASS</code>
              -Secret ein. Der Login-Request schickt es als Header und bekommt den Magic-Link direkt als
              Antwort — ohne Email-Versand. Wird nur im Browser (localStorage) gespeichert.
            </p>
            <input
              type="password"
              value={bypassInput}
              onChange={(e) => setBypassInput(e.target.value)}
              placeholder="64-stelliges Hex-Secret"
              className="w-full rounded bg-slate-900 border border-slate-700 px-3 py-2 font-mono text-[11px] focus:outline-none focus:ring-2 focus:ring-court-500"
            />
            <div className="flex gap-2">
              <button
                onClick={saveBypass}
                className="rounded bg-slate-800 hover:bg-slate-700 px-3 py-1.5 text-slate-200"
              >
                Speichern
              </button>
              <button
                onClick={() => {
                  setBypassInput("");
                  setAuthBypass(null);
                }}
                className="rounded bg-slate-900 hover:bg-slate-800 border border-slate-700 px-3 py-1.5 text-slate-400"
              >
                Entfernen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
